import ReactDOM, { hydrateRoot } from 'react-dom/client';
import { PostHogProvider } from 'posthog-js/react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'reduxjs-toolkit-persist/integration/react';
import { HelmetProvider } from 'react-helmet-async';
import { Loading } from 'src/components/Loading';
import { DebugContextProvider } from 'src/contexts/DebugContext';
import { ForwardRefContextProvider } from 'src/contexts/ForwardRefContext';
import { SessionContextProvider } from 'src/contexts/SessionContext';
import { AudioContextProvider } from 'src/contexts/AudioContext';
import { ThreadInputBoxContextProvider } from 'src/contexts/ThreadInputBoxContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import store, { persistor } from 'src/store';
import { AuthContextProvider } from 'src/contexts/AuthContext';
import { Amplify } from 'aws-amplify';
import { initializeGTMDeferred } from './utils/analytics';
import { AppRoutes } from 'src/types';
import App from 'src/components/App';
import { env } from 'src/env';
import '@szhsin/react-menu/dist/index.css';
import '@szhsin/react-menu/dist/transitions/slide.css';
import 'react-responsive-modal/styles.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-loading-skeleton/dist/skeleton.css';
import 'react-toastify/dist/ReactToastify.css';
import 'swiper/css/navigation';
import 'swiper/css/a11y';
import 'swiper/css';
import 'src/styles/index.scss';
import MetaTags from 'src/analyticsComponents/MetaTags/MetaTags';
import { GoogleApiProvider } from 'src/contexts/GoogleApiContext';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from '@aws-amplify/core';
import { APP_ENV } from './constants';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: `${env.REACT_APP_COGNITO_USER_POOL_ID}`,
      userPoolClientId: `${env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID}`,
      loginWith: {
        oauth: {
          domain: `${env.REACT_APP_COGNITO_OATH_DOMAIN}`,
          scopes: [
            'profile',
            'email',
            'openid',
            'aws.cognito.signin.user.admin',
          ],
          redirectSignIn: [`${env.REACT_APP_NINJA_UI_URL}`],
          redirectSignOut: [`${env.REACT_APP_NINJA_UI_URL}`],
          responseType: 'code', // or 'token', note that REFRESH token will only be generated when the responseType is code
        },
      },
    },
  },
});

let cognitoCookieDomain = 'localhost';
try {
  cognitoCookieDomain = new URL(env.REACT_APP_NINJA_UI_URL).hostname;
} catch (error) {
  console.error('Invalid REACT_APP_NINJA_UI_URL:', env.REACT_APP_NINJA_UI_URL);
}

cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    domain: cognitoCookieDomain,
    path: '/',
    expires: 365, // Cookie expiration in days
    secure: cognitoCookieDomain !== 'localhost', // only for test on localhost
  }),
);

// Google Tag Manager
if (window.location.pathname !== AppRoutes.PAYMENT) {
  initializeGTMDeferred();
}

const googleClientId = env.REACT_APP_GOOGLE_CLIENT_ID as string;

const rootElement = document.getElementById('root');

const root = ReactDOM.createRoot(rootElement as HTMLElement);

const NinjaApp = () => (
  <PostHogProvider
    apiKey={env.REACT_APP_POSTHOG_KEY}
    options={{
      api_host: env.REACT_APP_POSTHOG_HOST,
      defaults: '2025-05-24',
      person_profiles: 'always', // This enables person profiles, set to "never" or "identified_only" if you don't want this
      capture_exceptions: true, // This enables capturing exceptions using Error Tracking, set to false if you don't want this
      debug: APP_ENV === 'development',
    }}
  >
    <BrowserRouter>
      <Provider store={store}>
        <PersistGate persistor={persistor} loading={<Loading />}>
          <AuthContextProvider>
            <ForwardRefContextProvider>
              <DebugContextProvider>
                <AudioContextProvider>
                  <SessionContextProvider>
                    <ThreadInputBoxContextProvider>
                      <GoogleOAuthProvider clientId={googleClientId}>
                        <GoogleApiProvider clientId={googleClientId}>
                          <HelmetProvider>
                            <MetaTags />
                            <App />
                          </HelmetProvider>
                        </GoogleApiProvider>
                      </GoogleOAuthProvider>
                    </ThreadInputBoxContextProvider>
                  </SessionContextProvider>
                </AudioContextProvider>
              </DebugContextProvider>
            </ForwardRefContextProvider>
          </AuthContextProvider>
        </PersistGate>
      </Provider>
    </BrowserRouter>
  </PostHogProvider>
);

if (rootElement?.hasChildNodes()) {
  hydrateRoot(rootElement, <NinjaApp />);
} else {
  root.render(<NinjaApp />);
}
